import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { FieldErrorsImpl, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@navi/web-ui/lib/primitives/Button/Button';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import Chip from '@navi/web-ui/lib/primitives/Chip/index';
import cx from 'classnames';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import {
  fetchSelectedQption,
  getQuestion,
  getSkipTracingQuestions,
  submitForm,
  submitFormForCallBridge
} from './actions';
import RenderingEngine from './component';
import styles from './index.module.scss';
import {
  ConnectedType,
  ECallingType,
  FEEDBACK_FORM_STATES,
  FeedbackFormKind,
  FeedbackPayload,
  IFeedbackFormSource,
  InteractionType,
  QuestionResponse
} from './interfaces/index.type';
import {
  createPayloadForFeedbackHistory,
  feedbackFromReducer,
  getSortedOption,
  mapQuestionToRadioOptions,
  selectedAdornmentStyles,
  selectedOptionChipStyles
} from './utlis';
import Loader from '../../../components/Loader/Loader';
import CancelCircleFilled from '../../../assets/icons/CancelCircledFilled';
import { fetchFeedbackHistory } from '../actions/overviewActions';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from '@cp/constants/StorageKeys';
import DefaultContacts from './component/Contacts/DefaultContacts';
import NextPreviousCaseActions from './component/NextPreviousCaseActions';
import NextPreviousCaseActionsRecommended from './component/NextPreviousCaseActionsRecommended';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { setAmeyoCallDetails, setBlankDisposition } from 'src/reducers/commonSlice';
import { createQueryParams, readQueryParams } from '@cp/utils/QueryParamsHelper';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import RadioGroup from '@navi/web-ui/lib/components/RadioGroup';
import { OLD_CALLING_TYPES, REVAMPED_CALLING_TYPES } from '../constants';
import {
  resetFeedbackQuestionTypes,
  setIsFeedbackFormDirty,
  setSkipTracingInitialQuestions
} from './reducer/feedbackSlice';
import { Radio, TextArea } from '@navi/web-ui/lib/primitives';
import {
  HIDE_COMMENTS_CALLING_TYPES,
  SKIP_TRACING_TYPES,
  SKIP_TRACING_TYPES_OPTIONS
} from '@cp/pages/CaseDetails/feedbackForm/constants';
import isLitmusExperimentEnabled from '@cp/utils/isLitmusExperimentEnabled';
import { LITMUS_EXPERIMENT_NAMES } from '@cp/constants/litmusExperimentNames';
import { Roles } from '@cp/pages/auth/constants/AuthConstants';
import { getTelephonesV2 } from '@cp/pages/CaseDetails/actions/casesDetailsActions';
import { LIMIT_TYPE } from '../interfaces/CaseDetail.type';
import {
  DateFormat,
  dateFormat,
  formatToStandardTimeInHours,
  timeStringToDate
} from '@cp/src/utils/DateHelper';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import CustomRadio from './CustomRadio';
import dayjs from 'dayjs';
import { getActivePendingActions } from '../components/Overview/PendingActions/actions';

interface FeedbackFromProps {
  isDisableNextPreviousCaseAction?: boolean;
}

const FeedbackFrom = (props: FeedbackFromProps) => {
  const { isDisableNextPreviousCaseAction } = props;

  const dispatch = useDispatch();
  const {
    loanId = '',
    customerId = '',
    caseReferenceId = '',
    caseBusinessVertical = ''
  } = useParams();
  const { AMEYO: ameyoQueryParams, ...restParams } = readQueryParams();
  const { register, handleSubmit, reset, setValue, getValues, formState, unregister, clearErrors } =
    useForm({
      mode: 'all',
      shouldUnregister: true
    });
  const { isDirty } = formState;

  const questions = useSelector((state: RootState) => state.feedBack.questionTypes);
  const telephones = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]?.telephonesv2
  );

  const snoozedCase = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.pause?.data?.isPaused
  );

  const user = useSelector((state: RootState) => state.common.userData);
  const ameyoCallDetails = useSelector((state: RootState) => state.common.ameyoCallDetails);
  const isTeamLead = useSelector((state: RootState) => state.common?.isTeamLead);
  const isFeedbackFormDirty = useSelector((state: RootState) => state.feedBack.isFeedbackFormDirty);
  const skipTracingInitialQuestions = useSelector(
    (state: RootState) => state.feedBack.skipTracingInitialQuestions
  );
  const editAccessFlag = useSelector(
    (state: RootState) =>
      state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.details?.data?.editAccessFlag
  );
  const isExternal = !user?.naviUser;
  const navigate = useNavigate();
  const airtelSelfCallRecordingEnabled = useSelector(
    (store: RootState) => store.common.featureFlags.airtelSelfCallRecordingEnabled
  );
  const actionEvents = useSelector((state: RootState) => state.common.actionEvents);
  const [currentCallType, setCurrentCallType] = useState<ECallingType>(
    ameyoQueryParams?.method as ECallingType
  );
  const [skipTracingType, setSkipTracingType] = useState<SKIP_TRACING_TYPES | null>(null);
  const fetchedCallingTypes = user?.revampedFeedbackViewAllowed
    ? REVAMPED_CALLING_TYPES
    : OLD_CALLING_TYPES;
  // @TODO : move to useReducer
  const [feedback, dispatchFeedback] = useReducer(feedbackFromReducer, {
    selectedChip: null,
    questionMap: null,
    error: null,
    telephone: telephones?.data?.[0]?.number,
    comment: '',
    loading: false,
    initialOptionId: '',
    initialQuestionId: '',
    interactionId: sessionStorage.getItem(SESSION_STORAGE_KEYS.INTERACTION_ID) || null,
    feedbackFormState: FEEDBACK_FORM_STATES.NOT_SUBMITED,
    isImageUpload: false,
    telephoneNotRequired: false
  });
  const interactionType = useMemo(() => {
    if (feedback?.interactionId && currentCallType === ECallingType.CALL_BRIDGE) {
      return InteractionType.CALL_BRIDGE;
    }
    return currentCallType as unknown as InteractionType;
  }, [feedback.interactionId, currentCallType]);

  const removeInteractionIdFromSessionStorage = () => {
    dispatchFeedback({
      type: FeedbackFormKind.SET_INTERACTION_ID
    });
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.INTERACTION_ID);
  };
  const resetFeedbackForm = () => {
    reset();
    dispatchFeedback({
      type: FeedbackFormKind.RESET_STATE
    });
  };
  const isGlobalAccessGiven = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const disableSubmitButton =
    !(feedback.questionMap || skipTracingInitialQuestions) ||
    (feedback.telephoneNotRequired ? false : !feedback.telephone) ||
    feedback.loading ||
    (!sessionStorage.getItem(SESSION_STORAGE_KEYS.INTERACTION_ID) &&
      currentCallType === ECallingType.CALL_BRIDGE) ||
    feedback.isImageUpload;
  const disableSubmitFlag = isGlobalAccessGiven
    ? disableSubmitButton || !editAccessFlag
    : disableSubmitButton;
  const disableInitialQuestions = isGlobalAccessGiven ? !editAccessFlag : false;

  const shouldCleanup = useSelector((state: RootState) => state?.feedBack?.clearInteraction);

  useEffect(() => {
    return () => {
      dispatch(resetFeedbackQuestionTypes());
      localStorage.removeItem(LOCAL_STORAGE_KEYS.AMEYO_INTERACTION_ID);
      dispatch(setIsFeedbackFormDirty(false));
    };
  }, []);

  const getCallingWindowStartTime = useSelector(
    (store: RootState) => store?.common?.callingWindow?.callingStartTime
  );
  const getCallingWindowEndTime = useSelector(
    (store: RootState) => store?.common?.callingWindow?.callingEndTime
  );

  const serverDate = useSelector((state: RootState) => state?.common?.serverDate);
  const caseRecommendation = useSelector(
    (state: RootState) => state.caseRecommendation.currentRecommendedCase
  );
  const date = dateFormat(new Date(serverDate), DateFormat.hh_mm_ss);

  const isPostMidnight = useMemo(() => {
    const time = dayjs(new Date());
    const hour = time.hour();
    return hour >= 12;
  }, []);

  const startTime = timeStringToDate(getCallingWindowStartTime);
  const endTime = timeStringToDate(getCallingWindowEndTime);
  const serverTime = timeStringToDate(date);

  const isCallingWindowDisabled = serverTime < startTime || serverTime > endTime;

  useEffect(() => {
    // isFeedbackFormDirty is used for handling three other fields which are not part of the form
    if (isDirty || isFeedbackFormDirty) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        event.returnValue = 'Are you sure you want to leave?';
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isDirty, isFeedbackFormDirty]);

  useEffect(() => {
    removeInteractionIdFromSessionStorage();
    resetFeedbackForm();
    setCurrentCallType(undefined);
  }, [shouldCleanup]);

  useEffect(() => {
    if (!questions?.length) return;
    const preSelected = questions?.[0]?.options?.find(option => option.metadata?.isDefault);
    if (preSelected) {
      handleSelectedChip(preSelected.text, preSelected.referenceId);
    }
  }, [questions]);

  useEffect(() => {
    const updatedMethod = ameyoQueryParams?.method as ECallingType;
    if (updatedMethod === ECallingType.AMEYO) {
      setCurrentCallType(updatedMethod);
      removeInteractionIdFromSessionStorage();
    }
  }, [ameyoQueryParams?.method]);

  useEffect(() => {
    resetFeedbackForm();
  }, [currentCallType]);

  useEffect(() => {
    if (interactionType === InteractionType.CALL_BRIDGE && !ameyoQueryParams) {
      setCurrentCallType(ECallingType.CALL_BRIDGE);
    }
  }, [interactionType]);

  useEffect(() => {
    if (feedback.interactionId && !ameyoQueryParams) {
      setCurrentCallType(ECallingType.CALL_BRIDGE);
    }
  }, [feedback.interactionId]);

  // Reset data on tab change
  useEffect(() => {
    if (!ameyoQueryParams?.method) {
      setCurrentCallType(undefined);
    }

    dispatchFeedback({
      type: FeedbackFormKind.SET_INTERACTION_ID
    });

    const selectedTelephone = telephones?.data?.[0]?.number;

    dispatchFeedback({
      type: FeedbackFormKind.UPDATE_TELEPHONES,
      payload: { telephone: selectedTelephone }
    });
  }, []);

  const [isSkipTracingExperimentEnabled, setIsSkipTracingExperimentEnabled] = useState(false);
  useEffect(() => {
    isLitmusExperimentEnabled(LITMUS_EXPERIMENT_NAMES.SKIP_TRACING_FEEDBACK_FORM, {
      'x-customer-id': user?.referenceId
    }).then(res => {
      setIsSkipTracingExperimentEnabled(res?.result);
    });
  }, []);

  useEffect(() => {
    if (interactionType && currentCallType) {
      if (currentCallType === ECallingType.SKIP_TRACING && isSkipTracingExperimentEnabled) {
        return;
      }
      dispatch(
        getQuestion({ loanaccountnumber: loanId, customerreferenceid: customerId, interactionType })
      );
    }
  }, [interactionType, currentCallType]);

  useEffect(() => {
    if (skipTracingType) {
      dispatch(
        getSkipTracingQuestions({
          loanaccountnumber: loanId,
          customerreferenceid: customerId,
          interactionType: skipTracingType,
          dispatchFeedback
        })
      );
    }
  }, [skipTracingType]);

  useEffect(() => {
    if (ameyoCallDetails.interactionId && currentCallType === ECallingType.AMEYO) {
      dispatchFeedback({
        type: FeedbackFormKind.UPDATE_TELEPHONES,
        payload: { telephone: ameyoCallDetails.recipientNumber }
      });
      dispatchFeedback({
        type: FeedbackFormKind.SET_INTERACTION_ID,
        payload: { interactionId: ameyoCallDetails.interactionId }
      });
      sessionStorage.setItem(SESSION_STORAGE_KEYS.INTERACTION_ID, ameyoCallDetails.interactionId);
      localStorage.setItem(LOCAL_STORAGE_KEYS.AMEYO_INTERACTION_ID, ameyoCallDetails.interactionId);
    }
  }, [ameyoCallDetails.interactionId]);

  const handleSelectedChip = useCallback(
    (selectedChip: string, refId: string) => {
      dispatchFeedback({
        type: FeedbackFormKind.SET_SELECTED_CHIP,
        payload: { selectedChip, initialOptionId: refId }
      });
      fetchOption(refId);
    },
    [interactionType]
  );

  const sucessCallback = (questionResponse: Array<QuestionResponse>) => {
    return dispatchFeedback({
      type: FeedbackFormKind.UPDATE_QUESTION_MAP,
      payload: { questionResponse }
    });
  };

  const failureCallback = () => {
    toggleLoading(false);
    toast('something went wrong please try again', { type: 'error' });
  };

  const toggleLoading = (loading = false) => {
    dispatchFeedback({
      type: FeedbackFormKind.SET_LOADING,
      payload: { loading }
    });
  };

  const fetchOption = useCallback(
    (refId: string) => {
      toggleLoading(true);
      fetchSelectedQption(
        loanId,
        customerId,
        refId,
        sucessCallback,
        interactionType,
        failureCallback
      );
    },
    [feedback, interactionType]
  );

  const fetchOptionSelect = (refId: string, questionId: string) => {
    toggleLoading(true);
    fetchSelectedQption(
      loanId,
      customerId,
      refId,
      sucessCallback,
      interactionType,
      failureCallback,
      questionId
    );
  };

  const removeSelectedChip = useCallback(() => {
    dispatchFeedback({ type: FeedbackFormKind.REMOVE_SELECTED_CHIP });
    dispatchFeedback({
      type: FeedbackFormKind.TELEPHONE_REQUIRED,
      payload: { telephoneNotRequired: false, telephone: telephones?.data?.[0]?.number }
    });
    reset();
  }, [telephones]);

  const sortedOptions = useMemo(() => {
    const sorted = [...(questions?.[0]?.options || [])];
    dispatchFeedback({
      payload: { initialQuestionId: questions?.[0]?.referenceId },
      type: FeedbackFormKind.SET_INITIAL_QUESTION_ID
    });
    return getSortedOption(sorted);
  }, [feedback.selectedChip, questions]);

  const initialQuestions = useMemo(() => {
    return questions && questions[0]?.answerType === 'RADIO_OPTIONS' ? (
      <div key={currentCallType}>
        <RadioGroup
          options={mapQuestionToRadioOptions(sortedOptions, true)}
          value={feedback.selectedChip ?? ''}
          name="radioOptions"
          disabled={disableInitialQuestions}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (disableInitialQuestions) return;
            const option = questions[0].options.find(option => option.text === e.target.value)!;
            handleSelectedChip(option?.text, option?.referenceId);
            dispatchFeedback({
              type: FeedbackFormKind.TELEPHONE_REQUIRED,
              payload: { telephoneNotRequired: option.metadata?.phoneNumberNotRequired }
            });
            addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Feedback_CustomerFeedback, {
              selectedFeedback: option?.text
            });
            dispatch(setIsFeedbackFormDirty(true));
          }}
        />
      </div>
    ) : (
      sortedOptions?.map(option => {
        if (!feedback.selectedChip)
          return (
            <Chip
              key={option.referenceId}
              label={option.text}
              wrapperClasses={cx([
                styles.chip,
                {
                  [styles.chipActiveColor]: option.text === feedback.selectedChip
                }
              ])}
              labelClasses={styles.text}
              onChipClick={() => {
                if (disableInitialQuestions) return;
                handleSelectedChip(option.text, option.referenceId);
                dispatchFeedback({
                  type: FeedbackFormKind.TELEPHONE_REQUIRED,
                  payload: { telephoneNotRequired: option.metadata?.phoneNumberNotRequired }
                });
                addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Feedback_CustomerFeedback, {
                  selectedFeedback: option.text
                });
                dispatch(setIsFeedbackFormDirty(true));
              }}
              disabled={disableInitialQuestions}
            />
          );
        if (option.text !== feedback.selectedChip) return null;
        return (
          <Chip
            key={option.referenceId}
            label={option.text}
            disabled={disableInitialQuestions}
            wrapperClasses={cx([
              styles.chip,
              {
                [selectedOptionChipStyles(option?.metadata?.interactionLevel)]:
                  option.text === feedback.selectedChip
              }
            ])}
            RightAdornment={
              <CancelCircleFilled
                fillColor={selectedAdornmentStyles(option?.metadata?.interactionLevel)}
              />
            }
            labelClasses={styles.text}
            onChipClick={removeSelectedChip}
          />
        );
      })
    );
  }, [currentCallType, sortedOptions, loanId, customerId, feedback]);

  const customRenderOptions = isCallingWindowDisabled ? ['SKIP_TRACING_CALL'] : [];
  const customDisableOptions = isCallingWindowDisabled ? ['SKIP_TRACING_CALL'] : [];

  const skipTracingTypeSelect = useMemo(() => {
    return (
      <>
        <RadioGroup
          options={SKIP_TRACING_TYPES_OPTIONS}
          value={skipTracingType as string}
          disableSelectedOptions={customDisableOptions}
          name="radioOptions"
          renderOption={(option, props) => {
            if (customRenderOptions.includes(option.value as string)) {
              return <CustomRadio {...props} />;
            }
            return <Radio {...props} />;
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSkipTracingType(e.target.value as SKIP_TRACING_TYPES);
            dispatch(setIsFeedbackFormDirty(true));
            dispatchFeedback({
              type: FeedbackFormKind.TELEPHONE_REQUIRED,
              payload: { telephoneNotRequired: true }
            });
          }}
          disabled={disableInitialQuestions}
        />
      </>
    );
  }, []);

  const handleSubmitForm = useCallback(
    (formData: any) => {
      dispatchFeedback({ type: FeedbackFormKind.HANDLE_FORM_ERROR, payload: {} });
      if (!currentCallType) {
        toast('Please select calling type', { type: 'error' });
        return;
      }
      dispatch({ type: FeedbackFormKind.BEFORE_API_CALL });
      let key: keyof typeof formData;
      const feedbacks = [];
      if (!(isSkipTracingExperimentEnabled && currentCallType === ECallingType.SKIP_TRACING)) {
        feedbacks.push({
          questionReferenceId: feedback.initialQuestionId,
          optionReferenceId: feedback.initialOptionId
        });
      }
      for (key in formData) {
        const questionReferenceId = key.replace(`${IFeedbackFormSource.FEEDBACK_FORM}_`, '');
        if (questionReferenceId.includes('_option') && formData[key]) {
          feedbacks.push({
            questionReferenceId: questionReferenceId.replace('_option', ''),
            optionReferenceId: formData[key]
          });
        } else if (questionReferenceId.includes('_checkboxOption') && formData[key]) {
          if (Array.isArray(formData[key]) && formData[key]?.length > 0) {
            formData[key]?.forEach((option: unknown) => {
              feedbacks.push({
                questionReferenceId: questionReferenceId.replace('_checkboxOption', ''),
                optionReferenceId: option
              });
            });
          }
        } else if (formData[key]) {
          feedbacks.push({
            questionReferenceId: questionReferenceId,
            answerText: formData[key]
          });
        }
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const selectedTelephone: {
        number: string;
        source: string;
        numberCustomId: string;
      } = telephones?.data
        ?.map(telephone => ({
          number: telephone.number,
          source: telephone.source,
          numberCustomId: telephone.numberCustomId
        }))
        ?.find(telephone => telephone.number === feedback.telephone);

      const payload: FeedbackPayload = {
        feedbacks,
        accountNumber: loanId,
        agentPhoneNumber: user?.phoneNumber || '',
        interactionReminderDTO: null,
        recipientName: user?.name || '',
        recipientPhoneNumber: selectedTelephone?.number || '',
        recipientPhoneNumberCustomId: selectedTelephone?.numberCustomId || '',
        sourceType: selectedTelephone?.source
          ? `${selectedTelephone.source}_PHONE_CALL`
          : 'NO_SOURCE_SELECTED',
        comments: feedback.comment || '',
        interactionType:
          isSkipTracingExperimentEnabled && currentCallType === ECallingType.SKIP_TRACING
            ? skipTracingType
            : currentCallType
      };

      toggleLoading(true);

      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Feedback_SubmitFeedback, {
        interactionId: feedback?.interactionId,
        ...payload
      });
      sessionStorage.getItem(SESSION_STORAGE_KEYS.INTERACTION_ID) || ameyoQueryParams
        ? submitFormForCallBridge(
            loanId,
            customerId,
            payload,
            feedbackSubmittedSuccessfully,
            feedbackSubmissionFailed,
            (feedback.interactionId ||
              sessionStorage.getItem(SESSION_STORAGE_KEYS.INTERACTION_ID) ||
              localStorage.getItem(LOCAL_STORAGE_KEYS.AMEYO_INTERACTION_ID)) ??
              ''
          )
        : submitForm(
            loanId,
            customerId,
            payload,
            feedbackSubmittedSuccessfully,
            feedbackSubmissionFailed
          );
      dispatch(setIsFeedbackFormDirty(false));
      if (actionEvents?.length !== 0) {
        dispatch(getActivePendingActions(caseReferenceId, loanId, customerId));
      }
    },
    [user, feedback, loanId, currentCallType]
  );

  const disableWhenCallingWindowOver = (callingTypeValue: string) => {
    if (
      callingTypeValue === ECallingType.SELF_CALL ||
      callingTypeValue === ECallingType.WHATSAPP_CALL ||
      callingTypeValue === ECallingType.AMEYO ||
      callingTypeValue === ECallingType.SLASH
    ) {
      return isCallingWindowDisabled;
    }
    return false;
  };

  const disableWhenSnoozed = (callingTypeValue: string) => {
    if (snoozedCase && callingTypeValue === ECallingType.SKIP_TRACING) {
      return true;
    }

    return false;
  };

  const isCallingTypeOptionDisable = (callingTypeValue: string) => {
    return (
      callingTypeValue === ECallingType.CALL_BRIDGE ||
      callingTypeValue === ECallingType.AMEYO ||
      interactionType === InteractionType.CALL_BRIDGE ||
      interactionType === InteractionType.AMEYO
    );
  };

  const disableSelfCall = (callingTypeValue: string) => {
    return false;
  };
  //  the self-call button remains enabled for all users on Longhorn, regardless of the roll-out flag status (NTP-25636)

  const disableWhenLimitIsReached = (callingTypeValue: string) => {
    if (
      !(
        callingTypeValue === ECallingType.SELF_CALL ||
        callingTypeValue === ECallingType.WHATSAPP_CALL
      )
    ) {
      return false;
    }
    const selectedTelephone = feedback?.telephone;

    const telephoneData = telephones?.data?.find(
      telephone => telephone.number === selectedTelephone
    );

    if (!telephoneData) {
      return true;
    }

    if (
      telephoneData?.limit?.[LIMIT_TYPE.DAILY]?.callRemaining === 0 ||
      telephoneData?.limit?.[LIMIT_TYPE.HOURLY]?.callRemaining === 0
    ) {
      return true;
    }
    return false;
  };

  const feedbackSubmittedSuccessfully = useCallback(
    feedbackPayload => {
      setCurrentCallType(undefined);
      if (feedback.interactionId) {
        dispatch(setBlankDisposition({}));
      }
      dispatchFeedback({
        type: FeedbackFormKind.SUCESSFUL_API_CALL,
        payload: { telephone: telephones?.data?.[0].number }
      });

      dispatchFeedback({ type: FeedbackFormKind.SET_INTERACTION_ID });
      const payload = createPayloadForFeedbackHistory(
        loanId,
        caseReferenceId,
        caseBusinessVertical
      );
      dispatch(fetchFeedbackHistory(loanId, customerId, payload));
      dispatch(resetFeedbackQuestionTypes());
      dispatch(setSkipTracingInitialQuestions([]));
      dispatch(
        getTelephonesV2(loanId, customerId, caseReferenceId, caseBusinessVertical, successCallBack)
      );
      setSkipTracingType(null);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.AMEYO_INTERACTION_ID);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.INTERACTION_ID);
      setAmeyoCallDetails({ interactionId: '', recipientNumber: '', crtObjectId: '' });
      toggleLoading();
      reset();
      if (ameyoQueryParams) {
        const url = createQueryParams({
          ...restParams
        });
        navigate(url, {
          replace: true
        });
      }
    },
    [telephones]
  );
  const successCallBack = () => {
    dispatchFeedback({
      type: FeedbackFormKind.UPDATE_TELEPHONES,
      payload: { telephone: telephones?.data?.[0]?.number }
    });
  };
  const feedbackSubmissionFailed = useCallback(feedbackPayload => {
    dispatchFeedback({ type: FeedbackFormKind.API_ERROR });
    toggleLoading();
  }, []);

  const handleCommentChange = useCallback((changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    dispatchFeedback({
      type: FeedbackFormKind.UPDATE_COMMENT,
      payload: { comment: changeEvent.target.value }
    });
  }, []);

  const handleFeedbackFormError = useCallback(
    (
      error: Partial<
        FieldErrorsImpl<{
          [x: string]: any;
        }>
      >
    ) => {
      dispatchFeedback({ type: FeedbackFormKind.HANDLE_FORM_ERROR, payload: { error } });
    },
    []
  );

  const showComments = useMemo(
    () => feedback.selectedChip && !HIDE_COMMENTS_CALLING_TYPES.includes(currentCallType),
    [currentCallType, feedback.selectedChip]
  );

  const isVendorPocEnabled = useSelector(
    (state: RootState) => state.common.featureFlags?.vendorPocEnabled || false
  );

  const shouldShowPreviousNextActions = !isDisableNextPreviousCaseAction && !isTeamLead;
  const shouldShowRecommendedActions = caseRecommendation?.customerReferenceId;

  return (
    <div key={`${loanId}_${customerId}_${feedback}`} className={styles.container}>
      <div className={styles.feedback}>
        <DefaultContacts
          key={ameyoQueryParams?.phoneNumber || ''}
          feedback={feedback}
          dispatchFeedback={dispatchFeedback}
          currentCallType={currentCallType}
        />
        <>
          <div className={styles.feedbackHeaderContainer}>
            <Typography className={styles.feedbackHeader} as="span" variant="h4">
              Choose Communication Type
            </Typography>
          </div>
          <div key={currentCallType} className={styles.questions}>
            {fetchedCallingTypes.map(callingType => {
              if (isExternal && callingType.isHiddenForExternal) return null;
              if (callingType.value === ECallingType.VENDOR_POC && !isVendorPocEnabled) return null;
              return (
                <>
                  <Tooltip placement="top">
                    <TooltipTrigger tooltipTriggerClassName={'tooltipTriggerWrapper'}>
                      <Chip
                        key={callingType.value + feedback.telephone}
                        label={callingType.label}
                        wrapperClasses={cx([
                          styles.chip,
                          { [styles.chipActiveColor]: callingType.value === currentCallType }
                        ])}
                        disabled={
                          isCallingTypeOptionDisable(callingType.value) ||
                          disableInitialQuestions ||
                          disableWhenLimitIsReached(callingType.value) ||
                          disableWhenCallingWindowOver(callingType.value) ||
                          disableWhenSnoozed(callingType.value) ||
                          disableSelfCall(callingType.value)
                        }
                        labelClasses={styles.text}
                        onChipClick={() => {
                          if (isCallingTypeOptionDisable(callingType.value)) return;
                          removeInteractionIdFromSessionStorage();
                          setCurrentCallType(callingType?.value);
                          addClickstreamEvent(
                            CLICKSTREAM_EVENT_NAMES.LH_Feedback_CallTypeChipSelect,
                            {
                              callingType: callingType.value
                            }
                          );
                          dispatch(setSkipTracingInitialQuestions([]));
                          setSkipTracingType(null);
                          dispatch(setIsFeedbackFormDirty(true));
                          dispatchFeedback({
                            type: FeedbackFormKind.TELEPHONE_REQUIRED,
                            payload: { telephoneNotRequired: false }
                          });
                        }}
                      />
                    </TooltipTrigger>
                    {disableWhenCallingWindowOver(callingType.value) && (
                      <TooltipContent className="tooltipWrapper">
                        {`Feedback for calls available from  
                        ${formatToStandardTimeInHours(getCallingWindowStartTime)} 
                        ${isPostMidnight ? 'tomorrow' : 'today'}`}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </>
              );
            })}
          </div>
          {currentCallType && (
            <div className={styles.feedbackHeaderContainer}>
              <Typography className={styles.feedbackHeader} variant="h4">
                {[ECallingType.SLASH, ECallingType.SELF_CALL].includes(currentCallType)
                  ? ConnectedType.CALL_CONNECTION
                  : [ECallingType.WHATSAPP_CALL, ECallingType.WHATSAPP_TEXT].includes(
                      currentCallType
                    ) && questions
                  ? questions?.[0]?.text
                  : currentCallType === ECallingType.SKIP_TRACING && isSkipTracingExperimentEnabled
                  ? ConnectedType.SKIP_TRACING
                  : ConnectedType.CONNECTED_CONTACT}
              </Typography>
            </div>
          )}
          <div className={styles.questions}>
            {currentCallType === ECallingType.SKIP_TRACING && isSkipTracingExperimentEnabled
              ? skipTracingTypeSelect
              : null}
          </div>
          {currentCallType === ECallingType.SKIP_TRACING && isSkipTracingExperimentEnabled ? (
            skipTracingInitialQuestions?.map(question => (
              <RenderingEngine
                fetchOption={fetchOptionSelect}
                register={register}
                question={question}
                key={question.referenceId}
                error={feedback.error}
                toggleLoading={toggleLoading}
                interactionType={skipTracingType}
                setValue={setValue}
                getValue={getValues}
                lan={loanId}
                customerId={customerId}
                source={IFeedbackFormSource.FEEDBACK_FORM}
                getValues={getValues}
                dispatchFeedback={dispatchFeedback}
                unregister={unregister}
                clearErrors={clearErrors}
              />
            ))
          ) : (
            <div className={styles.questions}>{initialQuestions}</div>
          )}
          <form onSubmit={handleSubmit(handleSubmitForm, handleFeedbackFormError)}>
            {feedback.questionMap?.map(question =>
              question ? (
                <RenderingEngine
                  fetchOption={fetchOptionSelect}
                  register={register}
                  question={question}
                  key={question.referenceId}
                  error={feedback.error}
                  toggleLoading={toggleLoading}
                  interactionType={interactionType}
                  setValue={setValue}
                  getValue={getValues}
                  lan={loanId}
                  customerId={customerId}
                  source={IFeedbackFormSource.FEEDBACK_FORM}
                  getValues={getValues}
                  dispatchFeedback={dispatchFeedback}
                  unregister={unregister}
                  clearErrors={clearErrors}
                />
              ) : null
            )}
            {showComments && (
              <div className={styles.input}>
                <TextArea
                  value={feedback.comment}
                  onChange={handleCommentChange}
                  labelClassName={styles.label}
                  placeholder="Type here..."
                  id="comments"
                  resize
                  fullWidth
                  label="Comments (If any)"
                  rows={4}
                  containerClassName={styles.textAreaContainer}
                  className={styles.textAreaContainer}
                />
              </div>
            )}
          </form>
        </>
      </div>
      <div className={styles.submitButtonSection}>
        {shouldShowPreviousNextActions &&
          (shouldShowRecommendedActions ? (
            <NextPreviousCaseActionsRecommended />
          ) : (
            <NextPreviousCaseActions />
          ))}
        <Button
          disabled={disableSubmitFlag}
          fullWidth
          type={'submit'}
          onClick={handleSubmit(handleSubmitForm, handleFeedbackFormError)}
        >
          Submit
        </Button>
      </div>
      <Loader show={feedback.loading || false} className={styles.loadingState} animate={false} />
    </div>
  );
};

export default React.memo(FeedbackFrom);
